import { useEffect, useState } from 'react'
import { filtro, getDocCollection } from '../../../firebase/CRUD'
import { useAuth } from '../../../hooks/AuthContext'
import {
    rankearAlunosPorPontos,
    rankearAlunosPorPresenca,
    rankearTurmasPorPercentual,
    rankearTurmasPorOferta,
} from '../../../utils/rankings'

/**
 * Seção de ranking exibida ao final do relatório mensal.
 *
 * DECISÃO DE DESIGN: o ranking de turmas usa os documentos `aulaTurma`
 * diretamente (filtrados por `data.mes`), em vez do agregado da coleção
 * `relatorio` que o resto de RelatorioMensal.js usa. Motivo: `relatorio`
 * agrupa várias turmas da MESMA faixa etária numa única linha (ex.: duas
 * turmas de "Juvenis" viram uma soma só) — não dá pra saber qual turma
 * individual teve o melhor percentual/oferta a partir dele. Os docs
 * `aulaTurma` já têm `percentual`/`totalOfertas` calculados por turma
 * individual desde o fechamento da chamada (ver AulaChamada.js), então
 * usá-los direto é mais simples e mais preciso.
 *
 * O ranking de alunos (pontos/presença) já tem um seletor de trimestre —
 * a mesma lógica usada na ficha do aluno (AlunosView.js).
 */
export default function RankingRelatorio(){
    const {usuario} = useAuth()
    const [aulaTurmas, setAulaTurmas] = useState([])
    const [alunos, setAlunos] = useState([])
    const [trimestre, setTrimestre] = useState(null)
    const [carregando, setCarregando] = useState(true)

    useEffect(()=>{
        const mes = new Date().getMonth() + 1
        let recebidos = 0
        const finalizarSeCompleto = () => {
            recebidos += 1
            if (recebidos >= 2) setCarregando(false)
        }

        filtro('aulaTurma', 'data.mes', '==', mes, (dados)=>{
            setAulaTurmas(dados || [])
            finalizarSeCompleto()
        }, usuario.idEscola)

        getDocCollection('alunos', (dados)=>{
            setAlunos(dados || [])
            finalizarSeCompleto()
        }, usuario.idEscola)
    },[])

    if(carregando) return <p>Carregando ranking...</p>

    const rankingPercentual = rankearTurmasPorPercentual(aulaTurmas, 5)
    const rankingOferta = rankearTurmasPorOferta(aulaTurmas, 5)
    const rankingPontos = rankearAlunosPorPontos(alunos, trimestre, 5)
    const rankingPresenca = rankearAlunosPorPresenca(alunos, trimestre, 5)

    return(
        <section style={{marginTop:'32px'}}>
            <h3>Ranking do Mês</h3>

            <div style={{display:'flex', gap:'8px', alignItems:'center', marginBottom:'12px'}}>
                <label>Trimestre (alunos): </label>
                <select value={trimestre === null ? 'todos' : trimestre} onChange={(e)=> setTrimestre(e.target.value === 'todos' ? null : Number(e.target.value))}>
                    <option value="todos">Ano todo</option>
                    <option value={0}>1º Trimestre</option>
                    <option value={1}>2º Trimestre</option>
                    <option value={2}>3º Trimestre</option>
                    <option value={3}>4º Trimestre</option>
                </select>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:'16px'}}>
                <div>
                    <h4>Turmas — % de Frequência</h4>
                    <ol>
                        {rankingPercentual.map((t)=> <li key={t.nome}>{t.nome} — {t.valor}%</li>)}
                        {rankingPercentual.length === 0 && <p>Sem aulas fechadas neste mês.</p>}
                    </ol>
                </div>

                <div>
                    <h4>Turmas — Oferta Total</h4>
                    <ol>
                        {rankingOferta.map((t)=> <li key={t.nome}>{t.nome} — R$ {t.valor.toFixed(2)}</li>)}
                        {rankingOferta.length === 0 && <p>Sem aulas fechadas neste mês.</p>}
                    </ol>
                </div>

                <div>
                    <h4>Alunos — Mais Pontos</h4>
                    <ol>
                        {rankingPontos.map((a)=> <li key={a.id}>{a.nome} ({a.turmaNome || 'sem turma'}) — {a.valor} pts</li>)}
                        {rankingPontos.length === 0 && <p>Sem dados de alunos.</p>}
                    </ol>
                </div>

                <div>
                    <h4>Alunos — Mais Presenças</h4>
                    <ol>
                        {rankingPresenca.map((a)=> <li key={a.id}>{a.nome} ({a.turmaNome || 'sem turma'}) — {a.valor}</li>)}
                        {rankingPresenca.length === 0 && <p>Sem dados de alunos.</p>}
                    </ol>
                </div>
            </div>
        </section>
    )
}
